import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, CardBody, Card } from 'reactstrap';
import { Tooltip, IconButton, Button, FormControl, InputLabel, Select, MenuItem, Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import HubIcon from '@mui/icons-material/OtherHouses';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';
import DriverHubAssignModal from '../../AddOrUpdate/components/DriverHubAssignModal';
import { ACCOUNT_LOIGN_RETRY_COUNT } from 'src/utils/pnd/PndConstants';
import PndRestApi from 'src/utils/pnd/PndRestApi';

const DRIVER_COLUMNS = [
  {
    key: 'driverName',
    header: 'Driver Name',
    sortable: true,
  },
  
  {
    key: 'userName',
    header: 'User Name',
    sortable: true,
  },
  {
    key: 'hubName',
    header: 'Hub',
    sortable: true,
  },
  // {
  //   key: 'status',
  //   header: 'Status',
  //   width: '100px',
  //   align: 'center',
  //   sortable: false,
  //   render: (value) => (
  //     <span
  //       className={`badge ${value ? 'bg-success' : 'bg-danger'}`}
  //       style={{ fontSize: '0.72rem', fontWeight: 700 }}
  //     >
  //       {value ? 'ACTIVE' : 'IN-ACTIVE'}
  //     </span>
  //   ),
  // },
];

const DriverDetails = ({
  reactTableData,
  isNoRecordFound,
  onAdd,
  onEdit,
  onDelete,
  onUnlock,
  setRedirect
}) => {
  const rows = reactTableData?.tableRowsData ?? [];

  const [selectedDriver, setSelectedDriver] = useState(
    rows.length > 0 ? rows[0] : null
  );
  const [viewAssignPopup, setAssignPopup] = useState(false);
  const [selectedHub, setSelectedHub] = useState('all');
  const [assignmentStatus, setAssignmentStatus] = useState('all');
  const [hubsList, setHubsList] = useState([]);

  useEffect(() => {
    const fetchHubs = async () => {
      try {
        const response = await PndRestApi.GetAllActiveHub();
        if (response.status === 200 && response.data.status === 200) {
          setHubsList(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching hubs:', error);
      }
    };
    fetchHubs();
  }, []);

  const filteredRows = rows.filter(driver => {
    // Filter by Assignment Status
    if (assignmentStatus === 'assigned' && !driver.hubName) return false;
    if (assignmentStatus === 'unassigned' && driver.hubName) return false;
    
    // Filter by specific Hub
    if (selectedHub !== 'all' && driver.hubName !== selectedHub) return false;
    
    return true;
  });

  useEffect(() => {
    if (filteredRows.length > 0) {
      if (!selectedDriver || !filteredRows.some(d => d.driverId === selectedDriver.driverId)) {
        setSelectedDriver(filteredRows[0]);
      }
    } else {
      setSelectedDriver(null);
    }
  }, [selectedHub, assignmentStatus, rows]);

  const handleAssignToHub = () => {
    setAssignPopup(true);
  };

  const handleAssignmentStatusChange = (val) => {
    setAssignmentStatus(val);
    if (val === 'unassigned') {
      setSelectedHub('all');
    }
  };

  return (
    <Col md={12} lg={12} style={{ marginTop: '20px' }}>
      <Row>
        {/* ── Left: Master List ───────────────────────────── */}
        <Col md={7} lg={7}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="assignment-status-filter-label">Filter By Status</InputLabel>
              <Select
                labelId="assignment-status-filter-label"
                value={assignmentStatus}
                label="Assignment"
                onChange={(e) => handleAssignmentStatusChange(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: 'white' }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="assigned">Assigned</MenuItem>
                <MenuItem value="unassigned">Unassigned</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="hub-filter-label">Filter by Hub</InputLabel>
              <Select
                labelId="hub-filter-label"
                value={selectedHub}
                label="Filter by Hub"
                onChange={(e) => setSelectedHub(e.target.value)}
                sx={{ borderRadius: 2, bgcolor: 'white' }}
                disabled={assignmentStatus === 'unassigned'}
              >
                <MenuItem value="all">All Hubs</MenuItem>
                {hubsList.map(hub => (
                  <MenuItem key={hub.hubId} value={hub.hubName}>{hub.hubName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <RadioSelectTable
            rows={filteredRows}
            columns={DRIVER_COLUMNS}
            rowKey="driverId"
            selectedKey={selectedDriver?.driverId}
            onSelect={setSelectedDriver}
            searchKeys={['driverName', 'employerName', 'userName', 'mobileNo']}
            searchPlaceholder="Search by Driver name, username, agency..."
            sortKey="driverName"
            headerTitle="Driver List"
            onAdd={onAdd}
            pageSizes={[5, 10, 20, 50]}
          />
        </Col>

        {/* ── Right: Detail Panel ─────────────────────────── */}
        <Col md={5} lg={5}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            minHeight: '520px',
            background: '#ffffff',
            overflow: 'hidden',
          }}>
            {selectedDriver ? (
              <>
                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  padding: '24px 20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <AccountCircleIcon style={{ color: '#ffffff', fontSize: '2.5rem', opacity: 0.9 }} />
                      <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                          {selectedDriver.driverName}
                        </h4>
                        <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                          @{selectedDriver.userName}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <span
                        className={`badge ${selectedDriver.status ? 'bg-success' : 'bg-danger'}`}
                        style={{ fontSize: '0.75rem', fontWeight: 700 }}
                      >
                        {selectedDriver.status ? 'ACTIVE' : 'IN-ACTIVE'}
                      </span>
                      
                      {selectedDriver.retryCount >= ACCOUNT_LOIGN_RETRY_COUNT && (
                        <Tooltip title="Unlock locked user account">
                          <IconButton
                            size="small"
                            onClick={() => onUnlock(selectedDriver)}
                            style={{ color: '#ef4444', backgroundColor: 'rgba(255,255,255,0.15)' }}
                          >
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Edit details">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(selectedDriver)}
                          style={{ color: '#ffffff', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete driver permanently">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(selectedDriver)}
                          style={{ color: '#d70a0a', backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Personal & Agency Info */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Driver Info
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Agency:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <BusinessIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedDriver.employerName || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">License Number:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <BadgeIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedDriver.licenseNo || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">License Expiry:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <CalendarTodayIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedDriver.licenseExpiry || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Contact Details */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Contact Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Mobile No:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <PhoneIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedDriver.mobileNo || '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Email Address:</span>
                          <span className="fw-bold small text-dark d-flex align-items-center gap-1">
                            <EmailIcon style={{ fontSize: '14px', color: '#64748b' }} />
                            {selectedDriver.emailId || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Assignments */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2"
                        style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Hub & Vehicle Mappings
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted small">Assigned Hub:</span>
                          {selectedDriver.hubName ? (
                            <span className="badge bg-primary text-wrap" style={{ fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}>
                              <HubIcon style={{ fontSize: '14px' }} />
                              {selectedDriver.hubName}
                            </span>
                          ) : (
                            <span className="badge bg-secondary" style={{ fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}>
                              <HubIcon style={{ fontSize: '14px' }} />
                              Unassigned
                            </span>
                          )}
                        </div>

                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted small">Assigned Rider:</span>
                          {selectedDriver.vehicleId ? (
                            <span className="badge bg-success text-wrap" style={{ fontSize: '0.78rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}>
                              <DirectionsCarIcon style={{ fontSize: '14px' }} />
                              Assigned
                            </span>
                          ) : (
                            <span className="badge bg-secondary" style={{ fontSize: '0.75rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px' }}>
                              <DirectionsCarIcon style={{ fontSize: '14px' }} />
                              Not Assigned
                            </span>
                          )}
                        </div>

                        <div className="mt-2 pt-2 border-top d-flex justify-content-end">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<HubIcon />}
                            onClick={handleAssignToHub}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              borderRadius: '8px',
                              padding: '6px 16px',
                              background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                              color: '#ffffff',
                              boxShadow: '0 2px 4px 0 rgba(109, 40, 217, 0.2)',
                              border: 'none',
                              transition: 'all 0.2s ease-in-out',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
                                boxShadow: '0 4px 12px 0 rgba(109, 40, 217, 0.3)',
                                transform: 'translateY(-1px)',
                              },
                              '&:active': {
                                transform: 'translateY(0)',
                              }
                            }}
                          >
                            Manage Hub
                          </Button>
                        </div>
                      </div>
                    </div>

                  </div>
                </CardBody>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
              }}>
                <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>🛵</div>
                <h5 className="fw-bold text-muted">No Driver Selected</h5>
                <p className="text-muted small">Select a driver from the list to view details.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {viewAssignPopup && (
        <DriverHubAssignModal
          setRedirect={setRedirect}
          modalShow={viewAssignPopup}
          setModalShow={setAssignPopup}
          hubData={selectedDriver}
          isTitle="ASSIGN HUB"
        />
      )}
    </Col>
  );
};

DriverDetails.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUnlock: PropTypes.func.isRequired,
  setRedirect: PropTypes.func.isRequired,
};

export default DriverDetails;
